import React, { useEffect, useRef } from 'react'
import styled, { css, DefaultTheme } from 'styled-components'
import { Icon } from '~common/components/icon'
import { useTheme } from '~common/theme/theme-provider'
import { Combobox } from '@base-ui-components/react/combobox'
import { useLogic } from '~common/ui-logic/hooks/logic'
import { TextFieldLogic } from './logic'
import { Button } from '~common/components/button'

import { SortIndicator } from '~common/components/sort-indicator'
import { ThemeBorderRadius } from '~common/theme/types'

export interface TextFieldProps {
    /** The label text to display above the field */
    label?: string
    /** The content to display in the field - can be text or React elements */
    children?: React.ReactNode
    /** Optional icon name to display before the content */
    icon?: string | React.ReactNode
    /** Optional custom class name */
    className?: string
    /** Whether this is a multiline text field */
    multiline?: boolean
    /** Whether this is a dropdown field */
    dropdown?: boolean
    /** Options for dropdown field. Can be a list of labels (string[]), or a
     * dictionary mapping keys (stored values) to labels (display values).
     */
    options?: string[] | Record<string, string>
    /** Whether the text is editable */
    editable?: boolean
    /** Callback when text changes (only called when editable) */
    onChange?: (value: string) => void
    /** Debounce delay in milliseconds for onChange. Set to 0 or undefined to disable debouncing. Defaults to 300ms. */
    debounceMs?: number
    onClear?: (shiftKey?: boolean) => void
    /** Placeholder text for input fields */
    placeholder?: string
    /** Callback when key down is pressed */
    onConfirm?: (value: string) => void
    /** Whether the text field is active */
    initActive?: boolean
    initValue?: string
    /** Controlled value that updates the edit state when changed */
    controlledValue?: string
    disableOnBlur?: boolean
    /** Whether to call onConfirm on blur when value changes */
    confirmOnBlur?: boolean
    /** Callback when text field is clicked */
    onClick?: () => void
    /** Render action buttons */
    actionButtons?: React.ReactNode[]
    actionButtonsOnlyOnHover?: boolean
    inputValidation?: ['numbersOnly']
    autoFocus?: boolean
    /** Size preset for the input field */
    size?: 'small' | 'medium' | 'large'
    /** Custom height for the input field */
    height?: string | number
    /** Custom width for the input field */
    width?: string | number
    /** Variant for the text field, currently only supports 'tableHeader' */
    variant?: 'tableHeader' | 'tableCell'
    activated?: boolean
    id?: string
    onSort?: () => void
    sortDirection?: 'asc' | 'desc' | undefined
    textAlign?: 'left' | 'center' | 'right'
    resizable?: 'horizontal' | 'vertical' | 'both'
    /** Input type (e.g., 'text', 'email', 'password') */
    type?: 'text' | 'email' | 'password'
    /** Custom text color for the input */
    textColor?: string
    borderRadius?: keyof ThemeBorderRadius
}

export const TextField: React.FC<TextFieldProps> = (props) => {
    const { logic, state } = useLogic(TextFieldLogic, props)
    const theme = useTheme()

    const inputRef = React.useRef<HTMLInputElement | HTMLTextAreaElement>(null)
    const cbInputRef = React.useRef<HTMLInputElement>(null)
    const cbPopupRef = React.useRef<HTMLDivElement>(null)
    const rowRef = React.useRef<HTMLDivElement>(null)
    const prevIsActiveRef = React.useRef<boolean>(state.isActive)
    const isFirstRenderRef = React.useRef<boolean>(true)

    useEffect(() => {
        if (!state.isPopupOpen) {
            logic.setHasHighlighted(false)
            return
        }
        const popupEl = cbPopupRef.current
        if (!popupEl) {
            logic.setHasHighlighted(false)
            return
        }
        const check = () => {
            const exists = !!popupEl.querySelector('[data-highlighted]')
            logic.setHasHighlighted(exists)
        }
        check()
        const observer = new MutationObserver(check)
        observer.observe(popupEl, {
            subtree: true,
            attributes: true,
            attributeFilter: ['data-highlighted'],
        })
        return () => observer.disconnect()
    }, [state.isPopupOpen, logic])

    useEffect(() => {
        const row = rowRef.current
        if (!row) return
        const apply = () => logic.setAnchorWidth(row.offsetWidth)
        apply()
        const ro = new ResizeObserver(apply)
        ro.observe(row)
        window.addEventListener('resize', apply)
        return () => {
            ro.disconnect()
            window.removeEventListener('resize', apply)
        }
    }, [logic])

    useEffect(() => {
        const wasActive = prevIsActiveRef.current
        const isNowActive = state.isActive
        const isFirstRender = isFirstRenderRef.current

        if (props.editable && isNowActive && (!wasActive || isFirstRender)) {
            requestAnimationFrame(() => {
                const input = props.dropdown
                    ? cbInputRef.current
                    : inputRef.current
                if (input) {
                    const length = input.value.length
                    if (input.setSelectionRange) {
                        input.setSelectionRange(length, length)
                    }
                }
            })
        }

        if (isFirstRender) {
            isFirstRenderRef.current = false
        }
        prevIsActiveRef.current = isNowActive
    }, [state.isActive, props.editable, props.dropdown])

    const handleClick = () => {
        props.onClick?.()
        if (props.editable) {
            const wasActive = state.isActive
            if (
                state.editValue === props.initValue &&
                props.initActive != null &&
                !props.initActive
            ) {
                logic.setIsActive(true)
                logic.updateEditValue('')
            }
            requestAnimationFrame(() => {
                const input = props.dropdown
                    ? cbInputRef.current
                    : inputRef.current
                if (input) {
                    input.focus()
                    if (!wasActive) {
                        const length = input.value.length
                        if (input.setSelectionRange) {
                            input.setSelectionRange(length, length)
                        }
                    }
                }
            })
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        logic.handleKeyDown(e)
    }

    const handleBlur = () => {
        if (state.disableOnBlur) {
            return
        }
        logic.handleBlur()
    }

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        logic.handleInputChange(e.target.value)
    }

    const clearButton = (noAction?: boolean) => {
        if (
            props.editable &&
            (state.editValue?.length > 0 || (props.initValue?.length ?? 0) > 0)
        ) {
            return (
                <ButtonContainer
                    hasLabel={
                        (!!props.label || !!props.multiline) && state.isHovered
                    }
                >
                    <Button
                        icon="close"
                        size="sm"
                        onClick={(e) => {
                            if (noAction) {
                                return
                            }
                            e.preventDefault()
                            e.stopPropagation()
                            logic.handleClear(e.shiftKey)
                        }}
                        variant="glass"
                        status="error"
                        id="clear-button"
                        tabIndex={-1}
                    />
                </ButtonContainer>
            )
        }
    }

    const hasActionButtons = !!(
        (props.actionButtons?.length && props.actionButtons?.length > 0) ||
        props.onSort ||
        (props.editable &&
            (state.editValue?.length > 0 || (props.initValue?.length ?? 0) > 0))
    )

    return (
        <TextFieldContainer
            onBlur={handleBlur}
            isActive={state.isActive}
            onClick={handleClick}
            size={props.size}
            height={props.height}
            tabIndex={-1}
            borderRadius={props.borderRadius}
            hasLabel={!!props.label}
            editable={props.editable}
            multiline={props.multiline}
            width={props.width}
            variant={props.variant}
            initActive
            activated={props.activated}
            resizable={props.resizable}
            onMouseEnter={() => logic.setIsHovered(true)}
            onMouseLeave={() => logic.setIsHovered(false)}
        >
            {(props.label || props.multiline) &&
                !props.dropdown &&
                state.isHovered &&
                clearButton()}
            {props.label && (
                <FieldLabel size={props.size}>{props.label}</FieldLabel>
            )}
            <FieldValue
                icon={!!props.icon}
                hasLabel={!!props.label}
                size={props.size}
            >
                {props.icon &&
                    (typeof props.icon === 'string' ? (
                        <IconContainer>
                            <Icon
                                name={props.icon}
                                size={props.size}
                                iconColor={theme.theme.colors.greyScale[200]}
                            />
                        </IconContainer>
                    ) : (
                        <IconContainer>{props.icon}</IconContainer>
                    ))}
                <EditContainer size={props.size} hasLabel={!!props.label}>
                    {props.dropdown ? (
                        <Combobox.Root
                            value={state.editValue}
                            onValueChange={logic.handleComboboxValueChange}
                            items={state.dropdownLabels}
                            modal={false}
                            disabled={!props.editable}
                            onOpenChange={logic.handleComboboxOpenChange}
                        >
                            <ComboboxRow ref={rowRef}>
                                <CbInput
                                    placeholder={props.placeholder}
                                    autoFocus={props.autoFocus}
                                    ref={cbInputRef}
                                    onKeyDown={(e: React.KeyboardEvent) => {
                                        const query =
                                            cbInputRef.current?.value ?? ''
                                        logic.handleComboboxKeyDown(e, query)
                                    }}
                                    onChange={handleInputChange}
                                    onKeyUp={logic.handleComboboxKeyUp}
                                    size={props.size}
                                    hasLabel={!!props.label}
                                    isActive={state.isActive}
                                    value={state.editValue}
                                    onBlur={logic.handleSave}
                                    textAlign={props.textAlign}
                                />
                                <ActionButtonsDropdown>
                                    {state.editValue && (
                                        <CbClear aria-label="Clear selection">
                                            <Icon name="close" size="sm" />
                                        </CbClear>
                                    )}
                                    {(!state.editValue ||
                                        state.editValue === '') && (
                                        <CbOpen aria-label="Open Dropdown">
                                            <Icon
                                                name="triangle-down"
                                                size="sm"
                                            />
                                        </CbOpen>
                                    )}
                                </ActionButtonsDropdown>
                            </ComboboxRow>
                            <Combobox.Portal>
                                <CbPositioner sideOffset={12}>
                                    <CbPopup
                                        ref={cbPopupRef}
                                        $anchorWidth={state.anchorWidth}
                                    >
                                        <Combobox.List>
                                            {(item: string, index: number) => (
                                                <CbItem
                                                    key={item}
                                                    value={item}
                                                    data-prehighlight={
                                                        !state.hasHighlighted &&
                                                        !state.userNavigated &&
                                                        index === 0
                                                    }
                                                >
                                                    {item}
                                                </CbItem>
                                            )}
                                        </Combobox.List>
                                        {state.dropdownLabels.length === 0 && (
                                            <CbEmpty>No options found</CbEmpty>
                                        )}
                                    </CbPopup>
                                </CbPositioner>
                            </Combobox.Portal>
                        </Combobox.Root>
                    ) : props.multiline ? (
                        <TextArea
                            value={state.editValue}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            onKeyDown={handleKeyDown}
                            placeholder={props.placeholder}
                            autoFocus={props.autoFocus}
                            size={props.size}
                            hasLabel={!!props.label}
                            height={'100%'}
                            width={'100%'}
                            disabled={!props.editable}
                            ref={
                                inputRef as React.RefObject<HTMLTextAreaElement>
                            }
                        />
                    ) : (
                        <TextInput
                            type={props.type || 'text'}
                            value={state.editValue}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            onKeyDown={handleKeyDown}
                            placeholder={props.placeholder}
                            autoFocus={props.autoFocus}
                            isActive={state.isActive}
                            size={props.size}
                            height={props.height}
                            width={'100%'}
                            hasLabel={!!props.label}
                            disabled={!props.editable && !props.onClick}
                            ref={inputRef as React.RefObject<HTMLInputElement>}
                            textAlign={props.textAlign}
                            hasActionButtons={hasActionButtons}
                            textColor={props.textColor}
                        />
                    )}
                </EditContainer>

                {((props.actionButtonsOnlyOnHover && state.isHovered) ||
                    !props.actionButtonsOnlyOnHover) && (
                    <ActionButtons>
                        {!props.dropdown &&
                            !props.label &&
                            !props.multiline &&
                            clearButton()}
                        {props.onSort && (
                            <SortIndicator
                                key="sort"
                                initialDirection={props.sortDirection || 'desc'}
                                onSortChange={props.onSort}
                            />
                        )}
                        {props.actionButtons?.map((button, index) => (
                            <React.Fragment key={index}>
                                {button}
                            </React.Fragment>
                        ))}
                    </ActionButtons>
                )}
            </FieldValue>
        </TextFieldContainer>
    )
}

const TextFieldContainer = styled.div<{
    isActive: boolean
    size?: 'small' | 'medium' | 'large'
    height?: string | number
    hasText?: boolean
    hasLabel?: boolean
    editable?: boolean
    multiline?: boolean
    width?: string | number
    variant?: 'tableHeader' | 'tableCell'
    initActive?: boolean
    activated?: boolean
    resizable?: 'horizontal' | 'vertical' | 'both'
    theme: DefaultTheme
    borderRadius?: keyof ThemeBorderRadius
}>`
    width: ${({ width }) => (width ? width : '100%')};
    height: ${({ height }) => (height ? height : 'auto')};
    display: flex;
    position: relative;
    flex-direction: column;
    background: ${({ theme }) => theme.colors.background.bg1};
    outline: 1px solid ${({ theme }) => theme.colors.greyScale[300]};
    border-radius: ${({ theme, borderRadius }) =>
        theme.borderRadius[borderRadius || '2xl']};
    align-items: center;

    box-sizing: border-box;
    justify-content: center;
    ${({ isActive }) =>
        !isActive &&
        css`
            background-color: ${({ theme }) => theme.colors.background.bg1};
            font-weight: 600;
            outline: none;
        `}

    ${({ editable }) =>
        editable &&
        css`
            &:focus-within {
                outline: 1px solid ${({ theme }) => theme.colors.greyScale[300]};
            }
        `}
            
            
            ${({ hasLabel, multiline }) =>
        (hasLabel || multiline) &&
        css`
            justify-content: flex-start;
            border-radius: ${({ theme }) => theme.borderRadius['2xl']};
        `}

    ${({ variant, editable }) =>
        (variant === 'tableHeader' || variant === 'tableCell') &&
        css`
            border: none;
            outline: 1px solid transparent;
            padding-left: 0;
            padding-right: 0;
            border: none;
            outline: none;
            border-radius: ${({ theme }) => theme.borderRadius['md']};
            background: transparent;

            ${editable &&
            editable &&
            css`
                &:focus-within {
                    outline: none;
                    background-color: ${({ theme }) =>
                        theme.colors.background.bg2};
                }
            `}
        `}

    ${({ initActive }) =>
        initActive &&
        css`
            &:hover {
                background-color: ${({ theme }) => theme.colors.background.bg2};
            }
        `}
        ${({ editable }) =>
        !editable &&
        css`
            &:hover {
                background-color: unset;
            }
        `}
    ${({ activated }) =>
        activated &&
        css`
            outline: 1px solid ${({ theme }) => theme.colors.info};
        `}
    ${({ resizable }) =>
        resizable &&
        css`
            resize: ${resizable};
        `}
        ${({ initActive, variant }) =>
        initActive &&
        variant === 'tableCell' &&
        css`
            &:hover {
                background-color: ${({ theme }) => theme.colors.background.bg1};
            }
        `}
`

const FieldLabel = styled.span<{
    size?: 'small' | 'medium' | 'large'
    theme: DefaultTheme
}>`
    font-weight: 400;
    line-height: 1.3333333333333333em;
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    color: ${({ theme }) => theme.colors.greyScale[100]};
    padding: ${({ theme }) => `${theme.spacing[3]}`};
    text-align: left;
    width: 100%;
    box-sizing: border-box;

    ${({ size, theme }) => css`
        ${size === 'small' &&
        `
            padding: ${theme.spacing[2]};

        `}
        ${size === 'medium' &&
        `
            padding: ${theme.spacing[3]};

        `}
        ${size === 'large' &&
        `
                padding: ${theme.spacing[4]};

        `}
    `}
    padding-bottom: 0;
`

const FieldValue = styled.div<{
    hasLabel: boolean
    size?: 'small' | 'medium' | 'large'
    theme: DefaultTheme
    icon?: boolean
}>`
    display: flex;
    align-items: center;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.greyScale[0]};
    white-space: nowrap;
    position: relative;
    gap: ${({ theme }) => theme.spacing[3]};
    width: 100%;

    ${({ hasLabel, size = 'medium', theme }) => css`
        ${size === 'small' &&
        `
        font-size: ${theme.typography.fontSize.sm};
            line-height: 1.5em;
        `}
        ${size === 'medium' &&
        `
            font-size: ${theme.typography.fontSize.base};
            line-height: 1.4285714285714286em;
        `}
        ${size === 'large' &&
        `
            font-size: ${theme.typography.fontSize.lg};
            line-height: 1.375em;
        `}
    `}
    ${({ hasLabel }) =>
        hasLabel &&
        css`
            padding-top: 0;
        `}

    ${({ icon }) =>
        icon &&
        css`
            gap: ${({ theme }) => theme.spacing[0]};
        `}
    ${({ icon, hasLabel }) =>
        icon &&
        hasLabel &&
        css`
            gap: ${({ theme }) => theme.spacing[2]};
        `}
`

const EditContainer = styled.div<{
    size?: 'small' | 'medium' | 'large'
    hasLabel: boolean
}>`
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    width: 100%;
`

const TextInput = styled.input<{
    isActive: boolean
    size?: 'small' | 'medium' | 'large'
    height?: string | number
    width?: string | number
    hasLabel: boolean
    textAlign?: 'left' | 'center' | 'right'
    hasActionButtons?: boolean
    textColor?: string
    theme: DefaultTheme
}>`
    font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
    color: ${({ theme, textColor }) => textColor || theme.colors.text.primary};
    border: none;
    outline: none;
    background: transparent;
    margin: 0;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    width: 100%;

    ${({ size = 'medium', height, width, hasLabel }) => css`
        ${size === 'small' &&
        `
            font-size: 14px;

padding: ${hasLabel ? '0 12px 12px 12px' : '12px'};
            line-height: 1.5em;

        `}
        ${size === 'medium' &&
        `
            font-size: 16px;
            line-height: 1.4285714285714286em;
padding: ${hasLabel ? '12px 16px 12px 16px' : '8px 16px'};
        `}
        ${size === 'large' &&
        `
            font-size: 18px;
            line-height: 1.375em;
            padding: ${hasLabel ? '0 16px 20px 16px' : '20px 16px'};

        `}
        width: ${width
            ? typeof width === 'number'
                ? `${width}px`
                : width
            : '100%'};
        ${height &&
        `height: ${typeof height === 'number' ? `${height}px` : height};`}
    `}

    &::placeholder {
        color: ${({ theme }) => theme.colors.greyScale[100]};
        font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    }

    ${({ isActive }) =>
        !isActive &&
        css`
            font-weight: 600;
        `}

    ${({ textAlign }) =>
        textAlign &&
        css`
            text-align: ${textAlign};
        `}

    ${({ hasActionButtons }) =>
        hasActionButtons &&
        css`
            padding-right: 0;
        `}
`

const TextArea = styled.textarea<{
    size?: 'small' | 'medium' | 'large'
    height?: string | number
    width?: string | number
    hasLabel: boolean
    resizable?: 'vertical' | 'horizontal' | 'both'
    theme: DefaultTheme
}>`
    font-weight: 400;
    color: ${({ theme }) => theme.colors.text.primary};
    border: none;
    outline: none;
    background: transparent;
    padding: 0;
    margin: 0;
    resize: ${({ resizable }) => resizable || 'none'};
    box-sizing: border-box;

    ${({ size = 'medium', height, width, hasLabel }) => css`
        ${size === 'small' &&
        `
            font-size: 14px;
            line-height: 1.5em;
            min-height: 32px;
            padding: ${hasLabel ? '12px 12px 12px 12px' : '12px'};

        `}
        ${size === 'medium' &&
        `
            font-size: 16px;
            line-height: 1.4285714285714286em;
            min-height: 37px;
            padding: ${hasLabel ? '16px 12px 16px 12px' : '16px 12px'};


        `}
        ${size === 'large' &&
        `
            font-size: 18px;
            line-height: 1.375em;
            min-height: 44px;
            padding: ${hasLabel ? '20px 16px 20px 16px' : '20px 16px'};

        `}
        width: ${width
            ? typeof width === 'number'
                ? `${width}px`
                : width
            : '100%'};
        ${height &&
        `height: ${typeof height === 'number' ? `${height}px` : height};`}
    `}

    &::placeholder {
        color: ${({ theme }) => theme.colors.text.tertiary};
    }
`

const IconContainer = styled.div<{ theme: DefaultTheme }>`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-left: 15px;
    min-width: ${({ theme }) => theme.spacing[6]};
`

// Combobox styled parts
const ComboboxRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    position: relative;
    width: 100%;
    outline: none;
`

const CbInput = styled(Combobox.Input)<{
    size?: 'small' | 'medium' | 'large'
    height?: string | number
    width?: string | number
    hasLabel: boolean
    isActive: boolean
    textAlign?: 'left' | 'center' | 'right'
    theme: DefaultTheme
}>`
    box-sizing: border-box;

    margin: 0;
    border: none;
    width: 100%;
    height: 2.5rem;
    border-radius: 0.375rem;
    color: ${({ theme }) => theme.colors.text.primary};
    outline: none;
    background: transparent;

    ${({ size = 'medium', height, width, hasLabel }) => css`
        ${size === 'small' &&
        `
            font-size: 14px;

padding: ${hasLabel ? '12px 12px 12px 12px' : '12px'};
            line-height: 1.5em;

        `}
        ${size === 'medium' &&
        `
            font-size: 16px;
            line-height: 1.4285714285714286em;
padding: ${hasLabel ? '16px 12px 16px 12px' : '16px 12px'};
        `}
        ${size === 'large' &&
        `
            font-size: 18px;
            line-height: 1.375em;
            padding: ${hasLabel ? '20px 16px 20px 16px' : '20px 16px'};

        `}
        width: ${width
            ? typeof width === 'number'
                ? `${width}px`
                : width
            : '100%'};
        ${height &&
        `height: ${typeof height === 'number' ? `${height}px` : height};`}
    `}

    &::placeholder {
        color: ${({ theme }) => theme.colors.greyScale[100]};
        font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    }

    ${({ isActive }) =>
        !isActive &&
        css`
            font-weight: 600;
        `}
    outline: none;

    ${({ textAlign }) =>
        textAlign &&
        css`
            text-align: ${textAlign};
        `}
`

const ActionButtons = styled.div<{ theme: DefaultTheme }>`
    --size: 1.5rem;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    /* gap: ${({ theme }) => theme.spacing[2]}; */
    right: ${({ theme }) => theme.spacing[0.5]};
    border-radius: 0.25rem;
    border: none;
    color: ${({ theme }) => theme.colors.text.secondary};
    padding: 0;
    /* backdrop-filter: blur(2px);
    background-color: ${({ theme }) => theme.colors.background.bg2}01; */
`

const sharedButton = css<{ theme: DefaultTheme }>`
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--size, 1.5rem);
    height: 2.5rem;
    color: ${({ theme }) => theme.colors.text.secondary};
    border: none;
    padding: 0;
    border-radius: 0.25rem;
    background: none;
`

const CbClear = styled(Combobox.Clear)<{ theme: DefaultTheme }>`
    ${sharedButton}
    padding: 12px 12px 16px 12px;
`
const CbOpen = styled(Combobox.Trigger)<{ theme: DefaultTheme }>`
    ${sharedButton}
    padding: 12px 12px 16px 12px;
`

const CbPositioner = styled(Combobox.Positioner).attrs({ side: 'bottom' })`
    outline: 0;
    z-index: 1100;
`

const CbPopup = styled(Combobox.Popup)<{
    $anchorWidth?: number
    theme: DefaultTheme
}>`
    box-sizing: border-box;
    border-radius: 0.375rem;
    background-color: ${({ theme }) => theme.colors.background.bg1};
    color: ${({ theme }) => theme.colors.text.primary};
    width: ${({ $anchorWidth }) =>
        $anchorWidth ? `${$anchorWidth}px` : 'var(--anchor-width)'};
    max-height: 250px;
    padding: ${({ theme }) => theme.spacing[2]} 0;
    /* max-width: var(--available-width); */
    overflow-y: auto;
    scroll-padding-block: 0.5rem;
    overscroll-behavior: contain;
    box-shadow:
        0 0 5px rgba(59, 130, 246, 0.2),
        0 0 10px rgba(59, 130, 246, 0.2);

    transition:
        opacity 0.1s,
        transform 0.1s;
    transform-origin: var(--transform-origin);

    &[data-starting-style],
    &[data-ending-style] {
        opacity: 0;
        transform: scale(0.95);
    }

    @media (prefers-color-scheme: light) {
        outline: 1px solid ${({ theme }) => theme.colors.greyScale[300]};
        box-shadow:
            0 10px 15px -3px rgba(0, 0, 0, 0.1),
            0 4px 6px -4px rgba(0, 0, 0, 0.1);
    }

    @media (prefers-color-scheme: dark) {
        outline: 1px solid ${({ theme }) => theme.colors.greyScale[400]};
        outline-offset: -1px;
    }

    // hide scrollbar
    &::-webkit-scrollbar {
        display: none;
    }

    & {
        -ms-overflow-style: none;
    }

    & {
        scrollbar-width: none;
    }
`

const CbItem = styled(Combobox.Item)<{ theme: DefaultTheme }>`
    box-sizing: border-box;
    outline: 0;
    cursor: default;
    user-select: none;
    padding-block: 0.5rem;
    // prettier-ignore
    padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) =>
        theme.spacing[6]};
    display: grid;
    gap: 0.5rem;
    align-items: center;
    grid-template-columns: 0.75rem 1fr;
    font-size: 1rem;
    line-height: 1rem;
    white-space: nowrap;
    width: 100%;

    &[data-highlighted],
    &[data-prehighlight='true'] {
        z-index: 0;
        position: relative;
        color: ${({ theme }) => theme.colors.text.primary};
        font-weight: 600;
    }

    &[data-highlighted]::before,
    &[data-prehighlight='true']::before {
        content: '';
        z-index: -1;
        position: absolute;
        inset-block: 0;
        inset-inline: 0.5rem;
        border-radius: 0.25rem;
        width: 100%;
        background-color: ${({ theme }) => theme.colors.greyScale[400]};
    }
`

const CbEmpty = styled(Combobox.Empty)<{ theme: DefaultTheme }>`
    font-size: 0.925rem;
    line-height: 1rem;
    color: ${({ theme }) => theme.colors.text.secondary};
    // prettier-ignore
    padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) =>
        theme.spacing[6]};
`

const ActionButtonsDropdown = styled.div<{ theme: DefaultTheme }>`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${({ theme }) => theme.spacing[2]};
    position: absolute;
    right: ${({ theme }) => theme.spacing[2]};
`

const ButtonContainer = styled.div<{
    hasLabel?: boolean
    theme: DefaultTheme
}>`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    z-index: 1000;
    gap: ${({ theme }) => theme.spacing[2]};

    ${({ hasLabel }) =>
        hasLabel &&
        css`
            position: absolute;
            right: ${({ theme }) => theme.spacing[2]};
            top: ${({ theme }) => theme.spacing[2]};
        `}
`
